import { useRef } from 'react';

import { formatMoney } from '@betterment/core';
import { Box, Button, H2, PancakeStack, Separator, Sheet, Text, ToggleTip } from '@betterment/samba-react';

import { type SecurityGroupPosition, groupBuyableAndSellOnlyPositions, isSecurityPositionBySecurity } from './data';
import { SecurityPositionItem } from './SecurityPositionItem';
import { type SellButtonComponent, getSellButtonProps } from './sellButtonHelpers';

import { formatDrift, formatNumberWithDecimals } from '../../utils/NumbersHelper';

export const detailRowCss = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: 'text.weak',
};

type Props = {
  securityGroupPosition: SecurityGroupPosition;
  sellButton?: SellButtonComponent;
  groupSellOnlySubstitutes?: boolean;
};

export const SecurityGroupPositionSheet: React.FC<React.PropsWithChildren<Props>> = ({
  securityGroupPosition,
  sellButton: SellButton,
  groupSellOnlySubstitutes = false,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const hasMultipleSubAccounts = securityGroupPosition.securityPositionsBySecurity.some(
    sp => sp.securityPositionsBySubAccount.length > 1
  );

  const positions = hasMultipleSubAccounts
    ? securityGroupPosition.securityPositionsBySecurity
    : securityGroupPosition.securityPositions;

  const { buyablePositions, sellOnlySubstitutePositions } = groupSellOnlySubstitutes
    ? groupBuyableAndSellOnlyPositions(
        securityGroupPosition.securityGroup?.sellOnlySubstituteSecurities || [],
        positions
      )
    : { buyablePositions: positions, sellOnlySubstitutePositions: [] };

  return (
    <Sheet.Content
      css={{
        minWidth: '480px',
        p: 'space.50',
      }}
      ref={contentRef}
      size="medium"
      variant="right"
      tabIndex={-1}
      onOpenAutoFocus={e => {
        e.preventDefault();
        contentRef.current?.focus();
      }}
    >
      <PancakeStack css={{ height: '100%', overflowY: 'scroll', position: 'relative' }}>
        <PancakeStack.Header css={{ p: 'space.300', pt: 'space.400' }}>
          <Sheet.Title style={{ maxWidth: '90%' }}>{securityGroupPosition.securityGroup.name}</Sheet.Title>
          {securityGroupPosition.securityGroup.description && (
            <Sheet.Description>{securityGroupPosition.securityGroup.description}</Sheet.Description>
          )}
        </PancakeStack.Header>
        <PancakeStack.Main css={{ px: 'space.300', pb: 'space.300', flex: 1 }}>
          <Separator />
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100', py: 'space.200' }}>
            <Box css={detailRowCss}>
              <Text variant="caption">Current value</Text>
              <Text variant="caption">{formatMoney(securityGroupPosition.amount)}</Text>
            </Box>
            <Box css={detailRowCss}>
              <Text variant="caption">Annual fund fees</Text>
              <Box>
                <Text variant="caption">
                  {formatNumberWithDecimals(securityGroupPosition.averageExpenseRatioPercent)}%
                </Text>
                &nbsp;&middot;&nbsp;
                <Text variant="caption">{formatMoney(securityGroupPosition.fundFees)}</Text>
              </Box>
            </Box>
            <Box css={detailRowCss}>
              <Text variant="caption">Target weight</Text>
              <Text variant="caption">
                {formatNumberWithDecimals(securityGroupPosition.securityGroup.targetWeight, 1)}%
              </Text>
            </Box>
            <Box css={detailRowCss}>
              <Text variant="caption">Current weight</Text>
              <Text variant="caption">{formatNumberWithDecimals(securityGroupPosition.currentWeight, 1)}%</Text>
            </Box>
            <Box css={detailRowCss}>
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.25' }}>
                <Text variant="caption">Drift</Text>
                <ToggleTip>
                  <ToggleTip.Trigger iconName="InfoCircle" label="Drift" />
                  <ToggleTip.Content align="center" side="bottom" css={{ maxWidth: '320px' }}>
                    <Text>
                      Drift measures how much your assets move away from their target allocation due to your investments
                      growing at different rates.
                    </Text>
                  </ToggleTip.Content>
                </ToggleTip>
              </Box>
              <Text variant="caption">{formatDrift(securityGroupPosition.drift)}%</Text>
            </Box>
          </Box>
          <H2 variant="title200" css={{ pt: 'space.300', pb: 'space.200' }}>
            {buyablePositions.length === 1 ? 'Asset' : 'Assets'}
          </H2>
          <Box
            css={{ display: 'grid', gap: 'calc(token(spacing.space.200) + token(spacing.space.50))' }}
            data-testid="buyable-securities"
          >
            {buyablePositions.map(position => {
              const sellButtonProps = getSellButtonProps(position, securityGroupPosition);
              const key = isSecurityPositionBySecurity(position) ? position.id : position.security.id;
              return (
                <SecurityPositionItem
                  key={key}
                  securityPosition={position}
                  sellButton={SellButton && <SellButton {...sellButtonProps} />}
                />
              );
            })}
          </Box>
          {groupSellOnlySubstitutes && sellOnlySubstitutePositions.length > 0 && (
            <>
              <H2 variant="title200" css={{ pt: 'space.500', pb: 'space.200' }}>
                Sell-only substitutes
              </H2>
              <Box
                css={{ display: 'grid', gap: 'calc(token(spacing.space.200) + token(spacing.space.50))' }}
                data-testid="sell-only-substitute-securities"
              >
                {sellOnlySubstitutePositions.map(position => {
                  const sellButtonProps = getSellButtonProps(position, securityGroupPosition);
                  const key = isSecurityPositionBySecurity(position) ? position.id : position.security.id;

                  return (
                    <SecurityPositionItem
                      key={key}
                      securityPosition={position}
                      sellButton={SellButton && <SellButton {...sellButtonProps} />}
                    />
                  );
                })}
              </Box>
            </>
          )}
        </PancakeStack.Main>
        <PancakeStack.Footer
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            backgroundColor: 'background.surface',
            position: 'sticky',
            bottom: '0',
            gap: 'space.300',
            px: 'space.300',
            pb: 'space.300',
          }}
        >
          <Separator />
          <Sheet.Close asChild={true}>
            <Button variant="solidSecondary">Close</Button>
          </Sheet.Close>
        </PancakeStack.Footer>
      </PancakeStack>
    </Sheet.Content>
  );
};
